import React, { useEffect, useState } from 'react';

/**
 * "What parents say about {schoolName}" — aggregated Q15/Q16 counts from
 * /api/schools/{schoolId}/parent-signals. Replaces the old review synthesis
 * block. Threshold: show aggregated data only when ≥ 3 Switch Schools parents
 * have answered the questionnaire for this school; otherwise render the
 * "be the first" empty state.
 */
const MIN_SIGNALS_TO_DISPLAY = 3;

const ParentSignalsBlock = ({ schoolId, schoolName, apiUrl, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId || !apiUrl) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/schools/${schoolId}/parent-signals`);
        if (!res.ok) throw new Error('network');
        const json = await res.json();
        if (alive) setData(json);
      } catch {
        if (alive) setData({ total_signals: 0, whats_working: [], whats_not_working: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [schoolId, apiUrl]);

  const total = data?.total_signals || 0;
  const hasData = total >= MIN_SIGNALS_TO_DISPLAY
    && ((data?.whats_working?.length || 0) + (data?.whats_not_working?.length || 0) > 0);

  const paddingCls = compact ? 'px-4 py-4 mt-3' : 'px-5 sm:px-6 py-5';
  const titleCls = compact
    ? 'text-[11px] font-bold uppercase tracking-[0.18em] text-[#6B6560] mb-2'
    : 'text-sm font-bold text-[#2C2823] mb-3';
  const displayName = schoolName || 'this school';

  if (loading) return null;

  return (
    <div
      className={`${paddingCls} rounded-xl bg-[#FFF9F2] border border-[#F0E5D7]`}
      data-testid={`parent-signals-${schoolId}`}
    >
      <h4 className={titleCls}>
        {compact ? `What parents say about ${displayName}` : `What parents say about ${displayName}`}
      </h4>

      {hasData ? (
        <>
          {data.whats_working.length > 0 && (
            <div className="mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-1">
                Working well
              </p>
              <ul className="space-y-1">
                {data.whats_working.map((row) => {
                  const noun = row.count === 1 ? 'parent' : 'parents';
                  return (
                    <li
                      key={`w-${row.value}`}
                      className="text-sm text-[#2C2823] leading-snug"
                      data-testid={`parent-signal-working-${row.value}`}
                    >
                      <span aria-hidden="true" className="text-emerald-600 mr-1.5">•</span>
                      {row.count} {noun} said: {row.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {data.whats_not_working.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#B4532A] mb-1">
                What's not working
              </p>
              <ul className="space-y-1">
                {data.whats_not_working.map((row) => {
                  const noun = row.count === 1 ? 'parent' : 'parents';
                  return (
                    <li
                      key={`n-${row.value}`}
                      className="text-sm text-[#2C2823] leading-snug"
                      data-testid={`parent-signal-notworking-${row.value}`}
                    >
                      <span aria-hidden="true" className="text-[#B4532A] mr-1.5">•</span>
                      {row.count} {noun} said: {row.label}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <p className="mt-3 text-[10px] text-[#9B8F88]">
            Based on {total} Switch Schools {total === 1 ? 'parent' : 'parents'}.
          </p>
        </>
      ) : (
        <p className="text-sm text-[#2C2823] leading-snug" data-testid={`parent-signals-empty-${schoolId}`}>
          Be the first Switch Schools parent to share your experience —
          your questionnaire answers help other families.
        </p>
      )}
    </div>
  );
};

export default ParentSignalsBlock;
